import React, { createContext, useContext, useEffect, useState } from 'react';

export type AppConfig = {
  trialDays: number;
  requiresPaymentMethod: boolean;
};

const DEFAULT_CONFIG: AppConfig = {
  trialDays: 7,
  requiresPaymentMethod: true,
};

const RAW_API_BASE = ((import.meta as any)?.env?.VITE_TUBLY_API_BASE as string | undefined) ?? 'http://localhost:4000';
const API_BASE = RAW_API_BASE.replace(/\/$/, '');

export async function fetchAppConfig(): Promise<AppConfig> {
  const response = await fetch(`${API_BASE}/app/config`);

  if (!response.ok) {
    throw new Error(`Failed to fetch app config: ${response.status}`);
  }

  const jsonBody = await response.json();
  const parsedTrialDays = Number.isFinite(Number(jsonBody?.trialDays)) ? Number(jsonBody.trialDays) : DEFAULT_CONFIG.trialDays;
  const requiresPaymentMethod = typeof jsonBody?.requiresPaymentMethod === 'boolean'
    ? jsonBody.requiresPaymentMethod
    : DEFAULT_CONFIG.requiresPaymentMethod;

  return {
    trialDays: parsedTrialDays,
    requiresPaymentMethod,
  };
}

const AppConfigContext = createContext<AppConfig>(DEFAULT_CONFIG);

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [appConfig, setAppConfig] = useState<AppConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        const remoteConfig = await fetchAppConfig();

        if (!isCancelled) {
          setAppConfig((previous) => ({
            ...previous,
            ...remoteConfig,
          }));
        }
      } catch {
        // Keep defaults when config endpoint is unavailable
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <AppConfigContext.Provider value={appConfig}>
      {children}
    </AppConfigContext.Provider>
  );
};

export function useAppConfig(): AppConfig {
  const config = useContext(AppConfigContext);

  return config;
}
